import React, { useState, useEffect } from 'react';
import { useHistory, useParams, Link } from 'react-router-dom';
import SuperadminLayout from './SuperadminLayout';
import axios from '../../axios/axiosconf';
import './SuperadminStyles.css';

const UsuarioForm = () => {
    const history = useHistory();
    const { id, clienteId } = useParams();
    const isEditMode = !!id;

    const [formData, setFormData] = useState({
        client_id: clienteId || '',
        nombre: '',
        apellido: '',
        email: '',
        password: '',
        documento: '',
        telefono: '',
        especialidad_id: '',
        cargo_id: '',
        tipo_contrato_id: '',
        tipo_usuario: 0,
        activo: 1
    });
    const [clientes, setClientes] = useState([]);
    const [especialidades, setEspecialidades] = useState([]);
    const [cargos, setCargos] = useState([]);
    const [tiposContrato, setTiposContrato] = useState([]);
    const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
    const [generatedPassword, setGeneratedPassword] = useState('');
    const [errors, setErrors] = useState({});
    const [banner, setBanner] = useState({ type: '', message: '' });
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [usuarioOriginal, setUsuarioOriginal] = useState(null);

    const clearBanner = () => setBanner({ type: '', message: '' });

    const normalizeApiErrors = (error) => {
        const out = {};
        const data = error.response?.data;
        if (!data) {
            return { general: 'Error de red o del servidor. Intente de nuevo.' };
        }
        if (data.errors) {
            const raw = data.errors;
            Object.keys(raw).forEach((key) => {
                const v = raw[key];
                out[key] = Array.isArray(v) ? v[0] : String(v);
            });
            return out;
        }
        if (data.message) {
            if (typeof data.message === 'string' && data.message.toLowerCase().includes('email')) {
                return { email: data.message };
            }
            return { general: data.message };
        }
        return { general: `Error al ${isEditMode ? 'actualizar' : 'crear'} el usuario` };
    };

    const scrollToFirstError = (errKeys) => {
        const order = ['client_id', 'nombre', 'apellido', 'email', 'documento', 'tipo_usuario', 'password'];
        const first = order.find((k) => errKeys.includes(k)) || errKeys[0];
        if (!first) return;
        const el = document.getElementById(first === 'client_id' ? 'client_id' : first);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    const cuposLibresMedicos = clienteSeleccionado
        ? Math.max(0, (clienteSeleccionado.usuarios_medicos_disponibles || 0) - (clienteSeleccionado.usuarios_medicos_creados || 0))
        : 0;
    const cuposLibresAdmin = clienteSeleccionado
        ? Math.max(0, (clienteSeleccionado.usuarios_administrativos_disponibles || 0) - (clienteSeleccionado.usuarios_administrativos_creados || 0))
        : 0;
    const cuposLibresTipo = formData.tipo_usuario === 1 ? cuposLibresAdmin : cuposLibresMedicos;

    useEffect(() => {
        const token = localStorage.getItem('superadmin_token');
        const user = localStorage.getItem('superadmin_user');

        if (!token || !user) {
            history.push('/superadmin/login');
            return;
        }

        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        fetchInitialData();
    }, [history, id, isEditMode, clienteId]);

    const fetchInitialData = async () => {
        try {
            const [clientesRes, especialidadesRes, cargosRes, tiposContratoRes] = await Promise.all([
                axios.get('/superadmin/clientes'),
                axios.get('/superadmin/especialidades'),
                axios.get('/superadmin/cargos'),
                axios.get('/superadmin/tipos-contrato')
            ]);

            setClientes(Array.isArray(clientesRes.data) ? clientesRes.data : []);
            setEspecialidades(Array.isArray(especialidadesRes.data) ? especialidadesRes.data : []);
            setCargos(Array.isArray(cargosRes.data) ? cargosRes.data : []);
            setTiposContrato(Array.isArray(tiposContratoRes.data) ? tiposContratoRes.data : []);

            const clientesArray = Array.isArray(clientesRes.data) ? clientesRes.data : [];

            if (isEditMode) {
                const response = await axios.get(`/superadmin/usuarios/${id}`);
                const usuario = response.data;
                setFormData({
                    client_id: usuario.client_id || '',
                    nombre: usuario.name || '',
                    apellido: usuario.lastname || '',
                    email: usuario.email || '',
                    password: '',
                    documento: usuario.numero_documento || '',
                    telefono: usuario.celular || '',
                    especialidad_id: usuario.especialidad ?? '',
                    cargo_id: usuario.cargo ?? '',
                    tipo_contrato_id: usuario.tipo_contrato ?? '',
                    tipo_usuario: usuario.tipo_usuario !== null && usuario.tipo_usuario !== undefined
                        ? parseInt(usuario.tipo_usuario, 10)
                        : 0,
                    activo: usuario.activo === 0 || usuario.activo === '0' ? 0 : 1
                });
                setUsuarioOriginal({
                    activo: usuario.activo === 0 || usuario.activo === '0' ? 0 : 1,
                    tipo_usuario: usuario.tipo_usuario !== null && usuario.tipo_usuario !== undefined
                        ? parseInt(usuario.tipo_usuario, 10)
                        : 0,
                });
                const cliente = clientesArray.find((c) => c.id === usuario.client_id);
                setClienteSeleccionado(cliente || null);
            } else if (clienteId) {
                const cliente = clientesArray.find((c) => c.id === parseInt(clienteId, 10));
                setClienteSeleccionado(cliente || null);
            }

            setLoading(false);
        } catch (error) {
            console.error('Error fetching initial data:', error);
            if (error.response && error.response.status === 401) {
                localStorage.removeItem('superadmin_token');
                localStorage.removeItem('superadmin_user');
                history.push('/superadmin/login');
            }
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        let newValue = value;
        if (name === 'activo' || name === 'tipo_usuario') {
            newValue = value === '' ? '' : parseInt(value, 10);
        } else if (type === 'number') {
            newValue = parseInt(value, 10) || '';
        }
        setFormData({
            ...formData,
            [name]: newValue
        });

        if (name === 'client_id') {
            const cliente = clientes.find((c) => c.id === parseInt(value, 10));
            setClienteSeleccionado(cliente || null);
        }

        if (errors[name]) {
            setErrors({ ...errors, [name]: '' });
        }
        if (banner.message) {
            clearBanner();
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!isEditMode && !formData.client_id) {
            newErrors.client_id = 'El cliente es requerido';
        }

        if (!formData.nombre.trim()) {
            newErrors.nombre = 'El nombre es requerido';
        }

        if (!formData.apellido.trim()) {
            newErrors.apellido = 'El apellido es requerido';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'El email es requerido';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'El email no tiene un formato válido';
        }

        if (!isEditMode && !formData.password.trim()) {
            newErrors.password = 'La contraseña es requerida';
        } else if (!isEditMode && formData.password.trim() && formData.password.length < 6) {
            newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
        } else if (isEditMode && formData.password.trim() && formData.password.length < 6) {
            newErrors.password = 'Si indica contraseña, debe tener al menos 6 caracteres';
        }

        if (!formData.documento.trim()) {
            newErrors.documento = 'El documento es requerido';
        }

        const requiereCupo =
            formData.activo === 1 &&
            (!isEditMode ||
                !usuarioOriginal ||
                usuarioOriginal.activo !== 1 ||
                usuarioOriginal.tipo_usuario !== formData.tipo_usuario);

        if (requiereCupo && cuposLibresTipo <= 0) {
            newErrors.tipo_usuario = formData.tipo_usuario === 1
                ? 'No hay cupos disponibles para usuarios administrativos'
                : 'No hay cupos disponibles para usuarios médicos';
        }

        setErrors(newErrors);
        const keys = Object.keys(newErrors);
        if (keys.length > 0) {
            setBanner({
                type: 'warning',
                message: 'Revise los campos marcados con * o con error. Corrija la información e intente de nuevo.'
            });
            requestAnimationFrame(() => scrollToFirstError(keys));
        } else {
            clearBanner();
        }
        return keys.length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setSubmitting(true);
        clearBanner();
        setErrors({});

        try {
            const dataToSend = {
                nombre: formData.nombre.trim(),
                apellido: formData.apellido.trim(),
                email: formData.email.trim(),
                documento: formData.documento.trim(),
                telefono: (formData.telefono || '').trim(),
                especialidad_id: formData.especialidad_id || null,
                cargo_id: formData.cargo_id || null,
                tipo_contrato_id: formData.tipo_contrato_id || null,
                tipo_usuario: formData.tipo_usuario,
                activo: formData.activo === 1,
                estado: formData.activo === 1
            };

            if (isEditMode) {
                if (formData.password && formData.password.trim()) {
                    dataToSend.password = formData.password.trim();
                }
                await axios.put(`/superadmin/usuarios/${id}`, dataToSend);
                setBanner({
                    type: 'success',
                    message: 'Usuario actualizado correctamente. En un momento volverá a la lista de usuarios.'
                });
                setSubmitting(false);
                setTimeout(() => {
                    history.push(`/superadmin/clientes/${formData.client_id}/usuarios`);
                }, 1600);
            } else {
                dataToSend.client_id = formData.client_id;
                dataToSend.password = formData.password.trim();

                const response = await axios.post('/superadmin/usuarios', dataToSend);
                setSubmitting(false);
                if (response.data.password_temporal) {
                    setGeneratedPassword(response.data.password_temporal);
                } else {
                    setBanner({
                        type: 'success',
                        message: 'Usuario creado correctamente. Redirigiendo...'
                    });
                    setTimeout(() => {
                        history.push(`/superadmin/clientes/${formData.client_id}/usuarios`);
                    }, 1600);
                }
            }
        } catch (error) {
            console.error('Error saving usuario:', error);
            const apiErrors = normalizeApiErrors(error);
            setErrors(apiErrors);
            const keys = Object.keys(apiErrors);
            setBanner({
                type: 'danger',
                message:
                    apiErrors.general ||
                    'No se pudo guardar. Revise los mensajes bajo cada campo y corrija la información.'
            });
            const fieldKeys = keys.filter((k) => k !== 'general');
            if (fieldKeys.length > 0) {
                requestAnimationFrame(() => scrollToFirstError(fieldKeys));
            }
            setSubmitting(false);
        }
    };

    const requiereCupoSubmit =
        formData.activo === 1 &&
        (!isEditMode ||
            !usuarioOriginal ||
            usuarioOriginal.activo !== 1 ||
            usuarioOriginal.tipo_usuario !== formData.tipo_usuario);
    const limiteAlcanzado = requiereCupoSubmit && cuposLibresTipo <= 0;

    const formatCuposCliente = (cliente) => {
        const med = cliente.usuarios_medicos_creados ?? 0;
        const medDisp = cliente.usuarios_medicos_disponibles ?? 0;
        const adm = cliente.usuarios_administrativos_creados ?? 0;
        const admDisp = cliente.usuarios_administrativos_disponibles ?? 0;
        return `Méd: ${med}/${medDisp} · Admin: ${adm}/${admDisp}`;
    };

    if (loading) {
        return (
            <SuperadminLayout>
                <div className="loading-container">
                    <div className="spinner-border text-info" role="status">
                        <span className="sr-only">Cargando...</span>
                    </div>
                </div>
            </SuperadminLayout>
        );
    }

    if (generatedPassword) {
        return (
            <SuperadminLayout>
                <div className="alert alert-success">
                    <h4><i className="fas fa-check-circle"></i> Usuario creado exitosamente</h4>
                    <hr />
                    <h5>Contraseña temporal generada:</h5>
                    <h3 className="text-center my-4">
                        <code style={{ fontSize: '1.5rem', padding: '10px', background: '#f8f9fa', borderRadius: '5px' }}>
                            {generatedPassword}
                        </code>
                    </h3>
                    <p className="text-danger">
                        <i className="fas fa-exclamation-triangle"></i>{' '}
                        <strong>IMPORTANTE:</strong> Guarde esta contraseña. No se volverá a mostrar.
                    </p>
                </div>

                <div className="text-center mt-4">
                    <Link
                        to={`/superadmin/clientes/${formData.client_id}/usuarios`}
                        className="btn btn-superadmin btn-lg"
                    >
                        <i className="fas fa-arrow-left"></i> Volver a Usuarios
                    </Link>
                </div>
            </SuperadminLayout>
        );
    }

    return (
        <SuperadminLayout>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4>
                    <i className="fas fa-user"></i> {isEditMode ? 'Editar Usuario' : 'Nuevo Usuario'}
                </h4>
                <Link
                    to={clienteId ? `/superadmin/clientes/${clienteId}/usuarios` : '/superadmin/clientes'}
                    className="btn btn-secondary"
                >
                    <i className="fas fa-arrow-left"></i> Volver
                </Link>
            </div>

            {banner.message && (
                <div className={`alert alert-${banner.type}`} role="alert">
                    {banner.message}
                </div>
            )}

            <p className="text-muted small mb-3">
                Los campos marcados con <span className="text-danger">*</span> son obligatorios.
                El acceso al sistema es con <strong>email + contraseña</strong> (sin login legacy).
                Los usuarios creados desde superadmin quedan con acceso actualizado.
            </p>

            {!isEditMode && limiteAlcanzado && (
                <div className="alert alert-danger">
                    <i className="fas fa-exclamation-circle"></i> No hay cupos disponibles para el tipo de usuario seleccionado con estado activo.
                </div>
            )}

            <form onSubmit={handleSubmit}>
                <div className="form-section">
                    <h5>Información del Cliente</h5>

                    <div className="form-group">
                        <label htmlFor="client_id">
                            Cliente {!isEditMode && <span className="text-danger">*</span>}
                        </label>
                        <select
                            className={`form-control ${errors.client_id ? 'is-invalid' : ''}`}
                            id="client_id"
                            name="client_id"
                            value={formData.client_id}
                            onChange={handleChange}
                            disabled={isEditMode || !!clienteId}
                        >
                            <option value="">Seleccione un cliente</option>
                            {clientes.map((cliente) => (
                                <option key={cliente.id} value={cliente.id}>
                                    {cliente.nombre} - {cliente.nit} ({formatCuposCliente(cliente)})
                                </option>
                            ))}
                        </select>
                        {errors.client_id && (
                            <div className="invalid-feedback d-block">{errors.client_id}</div>
                        )}
                        {clienteSeleccionado && (
                            <small className="form-text text-muted">
                                Cupos médicos: {clienteSeleccionado.usuarios_medicos_creados || 0} / {clienteSeleccionado.usuarios_medicos_disponibles || 0}
                                {' · '}
                                Cupos admin: {clienteSeleccionado.usuarios_administrativos_creados || 0} / {clienteSeleccionado.usuarios_administrativos_disponibles || 0}
                            </small>
                        )}
                    </div>
                </div>

                <div className="form-section">
                    <h5>Información Personal</h5>

                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="nombre">Nombre <span className="text-danger">*</span></label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.nombre ? 'is-invalid' : ''}`}
                                    id="nombre"
                                    name="nombre"
                                    value={formData.nombre}
                                    onChange={handleChange}
                                    placeholder="Nombre del usuario"
                                />
                                {errors.nombre && (
                                    <div className="invalid-feedback d-block">{errors.nombre}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="apellido">Apellido <span className="text-danger">*</span></label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.apellido ? 'is-invalid' : ''}`}
                                    id="apellido"
                                    name="apellido"
                                    value={formData.apellido}
                                    onChange={handleChange}
                                    placeholder="Apellido del usuario"
                                />
                                {errors.apellido && (
                                    <div className="invalid-feedback d-block">{errors.apellido}</div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="email">Email <span className="text-danger">*</span></label>
                                <input
                                    type="email"
                                    className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="email@ejemplo.com"
                                    autoComplete="username"
                                />
                                {errors.email && (
                                    <div className="invalid-feedback d-block">{errors.email}</div>
                                )}
                                <small className="form-text text-muted">Será el usuario de ingreso al sistema.</small>
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="documento">Documento <span className="text-danger">*</span></label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.documento ? 'is-invalid' : ''}`}
                                    id="documento"
                                    name="documento"
                                    value={formData.documento}
                                    onChange={handleChange}
                                    placeholder="Número de documento"
                                />
                                {errors.documento && (
                                    <div className="invalid-feedback d-block">{errors.documento}</div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="telefono">Teléfono</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="telefono"
                                    name="telefono"
                                    value={formData.telefono}
                                    onChange={handleChange}
                                    placeholder="Teléfono de contacto"
                                />
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="password">
                                    Contraseña {!isEditMode && <span className="text-danger">*</span>}
                                </label>
                                <input
                                    type="password"
                                    className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                                    id="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    placeholder={isEditMode ? 'Dejar en blanco para no cambiar' : 'Contraseña de acceso'}
                                    autoComplete="new-password"
                                />
                                {errors.password && (
                                    <div className="invalid-feedback d-block">{errors.password}</div>
                                )}
                                <small className="form-text text-muted">
                                    {isEditMode ? 'Opcional en edición; mínimo 6 caracteres si la cambia.' : 'Mínimo 6 caracteres.'}
                                </small>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <h5>Información Profesional</h5>

                    <div className="row">
                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="especialidad_id">Especialidad</label>
                                <select
                                    className="form-control"
                                    id="especialidad_id"
                                    name="especialidad_id"
                                    value={formData.especialidad_id}
                                    onChange={handleChange}
                                >
                                    <option value="">Seleccione especialidad</option>
                                    {especialidades.map((esp) => (
                                        <option key={esp.id} value={esp.id}>{esp.nombre}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="cargo_id">Cargo</label>
                                <select
                                    className="form-control"
                                    id="cargo_id"
                                    name="cargo_id"
                                    value={formData.cargo_id}
                                    onChange={handleChange}
                                >
                                    <option value="">Seleccione cargo</option>
                                    {cargos.map((cargo) => (
                                        <option key={cargo.id} value={cargo.id}>{cargo.nombre}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="tipo_contrato_id">Tipo de Contrato</label>
                                <select
                                    className="form-control"
                                    id="tipo_contrato_id"
                                    name="tipo_contrato_id"
                                    value={formData.tipo_contrato_id}
                                    onChange={handleChange}
                                >
                                    <option value="">Seleccione tipo de contrato</option>
                                    {tiposContrato.map((tipo) => (
                                        <option key={tipo.id} value={tipo.id}>{tipo.nombre}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <h5>Configuración de Acceso</h5>

                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="tipo_usuario">
                                    Tipo de usuario <span className="text-danger">*</span>
                                </label>
                                <select
                                    className={`form-control ${errors.tipo_usuario ? 'is-invalid' : ''}`}
                                    id="tipo_usuario"
                                    name="tipo_usuario"
                                    value={formData.tipo_usuario}
                                    onChange={handleChange}
                                >
                                    <option value={0}>Médico / Especialista ({cuposLibresMedicos} cupos libres)</option>
                                    <option value={1}>Administrativo / Recepción ({cuposLibresAdmin} cupos libres)</option>
                                </select>
                                {errors.tipo_usuario && (
                                    <div className="invalid-feedback d-block">{errors.tipo_usuario}</div>
                                )}
                                <small className="form-text text-muted">
                                    Consume un cupo del tipo seleccionado si el estado es Activo.
                                </small>
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="activo">Estado de acceso</label>
                                <select
                                    className="form-control"
                                    id="activo"
                                    name="activo"
                                    value={formData.activo}
                                    onChange={handleChange}
                                >
                                    <option value={1}>Activo — puede ingresar al sistema</option>
                                    <option value={0}>Inactivo — solo personal, sin acceso</option>
                                </select>
                                <small className="form-text text-muted">
                                    Acceso actualizado automáticamente (usuario_actualizado = 1).
                                </small>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="form-group mt-4">
                    <button type="submit" className="btn btn-superadmin" disabled={submitting || (!isEditMode && limiteAlcanzado)}>
                        {submitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm mr-2"></span>
                                Guardando...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-save"></i> {isEditMode ? 'Actualizar Usuario' : 'Crear Usuario'}
                            </>
                        )}
                    </button>
                    <Link
                        to={clienteId ? `/superadmin/clientes/${clienteId}/usuarios` : '/superadmin/clientes'}
                        className="btn btn-secondary ml-2"
                    >
                        Cancelar
                    </Link>
                </div>
            </form>
        </SuperadminLayout>
    );
};

export default UsuarioForm;
